import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../context/AuthContext'; // Adjust the import path as necessary

const CreateOrder = () => {
  const { token: authToken } = useAuth(); // Access the token from AuthContext
  const [formData, setFormData] = useState({
    backupCode1: '',
    backupCode2: '',
    contactEmail: '',
    discountCode: '',
    orderType: 'buy', // Default to 'buy'
    originEmail: '',
    originPassword: '',
    type: 'PS', // Default type
    price: 0,
    quantity: 50000, // Default quantity
  });
  const [userId, setUserId] = useState(null); // State to store user ID


  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/users/me`, {
          headers: {
            Authorization: `Bearer ${authToken}`, // Use the token for authorization
          },
        });

        const userData = response.data;
        if (userData?._id) {
          setUserId(userData._id); // Set the user ID in state
          console.log("User ID:", userData._id); // Log the user ID
        } else {
          console.error("User ID not found in response.");
        }
      } catch (error) {
        console.error("Error fetching user ID:", error);
        toast.error("Failed to fetch user information.");
      }
    };

    fetchUserId();
  }, [authToken]);


  const [prices, setPrices] = useState({ PS: 0, XB: 0, PC: 0 });
  const [isDiscountApplied, setIsDiscountApplied] = useState(false); // Track if discount is applied

  // Fetch prices from your /prices endpoint
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/prices/`);
        const priceData = response.data[0];
        setPrices(priceData);
        setFormData((prevData) => ({
          ...prevData,
          price: priceData[prevData.type],
        }));
      } catch (error) {
        console.error('Error fetching prices:', error);
        toast.error('Failed to fetch prices.'); // Notify user on failure
      }
    };

    fetchPrices();
  }, []);

  // Fetch existing order data if userId is provided
  useEffect(() => {
    const loadOrderData = () => {
      const savedData = JSON.parse(localStorage.getItem(`orderData_${userId}`));
      if (savedData) {
        setFormData((prevData) => ({
          ...prevData,
          ...savedData,
        }));
      }
    };

    loadOrderData();
  }, [userId]);

  // Update price when type or quantity changes
  useEffect(() => {
    const pricePerUnit = prices[formData.type] || 0;
    const totalPrice = (formData.quantity / 50000) * pricePerUnit;
    setFormData((prevData) => ({ ...prevData, price: totalPrice }));
  }, [formData.type, formData.quantity, prices]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));

    // Save updated form data to local storage
    localStorage.setItem(`orderData_${userId}`, JSON.stringify({
      ...formData,
      [name]: value,
    }));
  };

  const handleDiscountApply = async () => {
    if (isDiscountApplied) {
      toast.error('Discount code has already been applied.');
      return; // Prevent further application if already applied
    }

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/coupons`);
      const coupons = response.data;

      const coupon = coupons.find(c => c.couponCode === formData.discountCode && c.status === 'active');

      if (coupon) {
        const discount = (coupon.discountPercentage / 100) * formData.price;
        const newPrice = formData.price - discount;
        setFormData(prevData => ({
          ...prevData,
          price: newPrice,
        }));
        setIsDiscountApplied(true); // Mark discount as applied
        toast.success(`Discount code applied! You saved ${coupon.discountPercentage}%.`);
      } else {
        toast.error('Invalid or inactive discount code.');
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
      toast.error('Failed to apply discount code.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const newFormData = {
        ...formData,
        userId: userId || null, // Ensure userId is included
      };

      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/orders/`, newFormData, {
        headers: {
          Authorization: `Bearer ${authToken}`, // Use the token from context
        },
      });

      toast.success('Order created successfully!');
      console.log("User ID:", userId);
      setIsDiscountApplied(false); // Reset discount state on successful order creation
    } catch (error) {
      console.error('Error creating order:', error);
      if (error.response && error.response.data) {
        // Provide more detailed error feedback if available
        toast.error(error.response.data.error || 'Error creating order, please try again.');
      } else {
        toast.error('Error creating order, please check your token and try again.');
      }
    }
  };

  return (
    <div>
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover draggable />
      <form onSubmit={handleSubmit} className="max-w-lg mx-auto p-4 bg-white rounded shadow-md">
        <h2 className="text-2xl font-bold mb-4">Create Order</h2>

        {/* Form fields */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Backup Code 1:</label>
          <input
            type="text"
            name="backupCode1"
            value={formData.backupCode1}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Backup Code 2:</label>
          <input
            type="text"
            name="backupCode2"
            value={formData.backupCode2}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Contact Email:</label>
          <input
            type="email"
            name="contactEmail"
            value={formData.contactEmail}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Discount Code:</label>
          <div className="flex">
            <input
              type="text"
              name="discountCode"
              value={formData.discountCode}
              onChange={handleChange}
              className="flex-1 border border-gray-300 rounded-l p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isDiscountApplied} // Disable input if discount is applied
            />
            <button
              type="button"
              onClick={handleDiscountApply}
              className="bg-blue-500 text-white rounded-r p-2 hover:bg-blue-600 transition duration-200"
              disabled={isDiscountApplied} // Disable button if discount is applied
            >
              APPLY
            </button>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Order Type:</label>
          <select
            name="orderType"
            value={formData.orderType}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="buy">Buy</option>
            <option value="sell">Sell</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">EA Email:</label>
          <input
            type="email"
            name="originEmail"
            value={formData.originEmail}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">EA Password:</label>
          <input
            type="password"
            name="originPassword"
            value={formData.originPassword}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Type:</label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="PS">PS</option>
            <option value="XB">XB</option>
            <option value="PC">PC</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Quantity:</label>
          <select
            name="quantity"
            value={formData.quantity}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={50000}>50K</option>
            <option value={100000}>100K</option>
            <option value={200000}>200K</option>
            <option value={300000}>300K</option>
            <option value={400000}>400K</option>
            <option value={1000000}>1M</option>
          </select>
        </div>


        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Price:</label>
          <input
            type="text"
            name="price"
            value={formData.price}
            readOnly
            className="w-full border border-gray-300 rounded p-2 bg-gray-200"
          />
        </div>

        <button
          type="submit"
          className="bg-green-500 text-white rounded p-2 hover:bg-green-600 transition duration-200"
        >
          Create Order
        </button>
      </form>
    </div>
  );
};

export default CreateOrder;
